import { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { MarkdownPreview } from '../../components/MarkdownPreview';
import { StarRatingDisplay } from '../../components/shared/StarRatingDisplay';
import { apiService } from '../../services/api';
import type { SessionDetailResponse, MessageItem } from '../../types';

// ---------------------------------------------------------------------------
// ユーティリティ
// ---------------------------------------------------------------------------

function formatDatetime(iso: string): string {
  return new Date(iso).toLocaleString('ja-JP', {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
  });
}

function formatTime(iso: string): string {
  return new Date(iso).toLocaleTimeString('ja-JP', {
    hour: '2-digit',
    minute: '2-digit',
  });
}

// ---------------------------------------------------------------------------
// サブコンポーネント
// ---------------------------------------------------------------------------

const messageTypeLabel: Record<MessageItem['messageType'], string> = {
  guide: 'ガイド',
  selection: '選択',
  input: 'テキスト入力',
  ai_response: 'AI回答',
  error: 'エラー',
};

const actionTypeLabel: Record<string, { label: string; icon: string }> = {
  repair_yes: { label: '修理依頼（はい）', icon: '🔧' },
  phone_click: { label: '電話番号クリック', icon: '📞' },
  repair_form_click: { label: '修理フォームクリック', icon: '📋' },
};

function MessageBubble({ message }: { message: MessageItem }) {
  const isUser = message.role === 'user';
  const isSystem = message.role === 'system';

  if (isSystem) {
    return (
      <div className="flex justify-center">
        <span className="rounded-full bg-slate-100 px-3 py-1 text-xs text-slate-400">
          {message.content}
        </span>
      </div>
    );
  }

  return (
    <div className={`flex gap-2 ${isUser ? 'flex-row-reverse' : 'flex-row'}`}>
      {/* アバター */}
      <div
        className={`flex h-8 w-8 shrink-0 items-center justify-center rounded-full text-xs font-bold ${
          isUser ? 'bg-blue-500 text-white' : 'bg-slate-200 text-slate-600'
        }`}
      >
        {isUser ? 'U' : 'AI'}
      </div>

      {/* バブル */}
      <div className={`max-w-lg ${isUser ? 'items-end' : 'items-start'} flex flex-col gap-1`}>
        <div
          className={`rounded-2xl px-4 py-2.5 text-sm leading-relaxed ${
            isUser
              ? 'rounded-tr-sm bg-blue-500 text-white'
              : 'rounded-tl-sm bg-slate-100 text-slate-800'
          }`}
        >
          <MarkdownPreview content={message.content} />
        </div>
        <div className="flex items-center gap-1.5 px-1 text-xs text-slate-400">
          <span>{formatTime(message.timestamp)}</span>
          <span>·</span>
          <span>{messageTypeLabel[message.messageType] ?? message.messageType}</span>
        </div>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// メインページ
// ---------------------------------------------------------------------------

export const ChatLogDetailPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const [detail, setDetail] = useState<SessionDetailResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!id) return;
    setLoading(true);
    setError(null);
    apiService
      .getSessionDetail(id)
      .then(setDetail)
      .catch(() => setError('会話ログの取得に失敗しました'))
      .finally(() => setLoading(false));
  }, [id]);

  if (loading) {
    return (
      <div className="space-y-4 p-6">
        <div className="h-8 w-48 animate-pulse rounded bg-slate-100" />
        <div className="h-32 animate-pulse rounded-xl bg-slate-100" />
        <div className="h-64 animate-pulse rounded-xl bg-slate-100" />
      </div>
    );
  }

  if (error || !detail) {
    return (
      <div className="p-6 text-center">
        <p className="mb-4 text-slate-600">{error ?? '会話ログが見つかりません'}</p>
        <Link to="/logs/chat" className="text-sm text-blue-600 hover:text-blue-800">
          ← チャットログ一覧に戻る
        </Link>
      </div>
    );
  }

  return (
    <div className="space-y-6 p-6">
      {/* ── パンくずリスト ── */}
      <nav className="flex items-center gap-1 text-sm text-slate-500">
        <Link to="/logs/chat" className="hover:text-blue-600">
          チャットログ
        </Link>
        <span>/</span>
        <span className="font-medium text-slate-700">会話詳細</span>
      </nav>

      {/* ── セッション情報 ── */}
      <section className="rounded-xl border border-slate-200 bg-white p-5 shadow-sm">
        <div className="flex flex-wrap items-center justify-between gap-4">
          <h1 className="text-lg font-bold text-slate-900">会話詳細</h1>
          <StarRatingDisplay rating={detail.rating} />
        </div>

        <dl className="mt-4 grid grid-cols-2 gap-x-6 gap-y-3 text-sm sm:grid-cols-3">
          <div>
            <dt className="font-medium text-slate-500">開始日時</dt>
            <dd className="mt-0.5 text-slate-800">{formatDatetime(detail.timestamp)}</dd>
          </div>
          <div>
            <dt className="font-medium text-slate-500">カテゴリ</dt>
            <dd className="mt-0.5 text-slate-800">{detail.category || '—'}</dd>
          </div>
          <div>
            <dt className="font-medium text-slate-500">メッセージ数</dt>
            <dd className="mt-0.5 text-slate-800">{detail.messages.length} 件</dd>
          </div>
        </dl>
      </section>

      {/* ── 会話履歴 ── */}
      <section className="rounded-xl border border-slate-200 bg-white shadow-sm">
        <div className="border-b border-slate-200 px-5 py-4">
          <h2 className="text-sm font-semibold text-slate-700">会話履歴</h2>
        </div>

        {detail.messages.length === 0 ? (
          <p className="p-6 text-center text-sm text-slate-500">メッセージがありません</p>
        ) : (
          <div className="space-y-4 p-5">
            {detail.messages.map((msg) => (
              <MessageBubble key={msg.messageId} message={msg} />
            ))}
          </div>
        )}
      </section>

      {/* ── アクション履歴 ── */}
      <section className="rounded-xl border border-slate-200 bg-white shadow-sm">
        <div className="border-b border-slate-200 px-5 py-4">
          <h2 className="text-sm font-semibold text-slate-700">アクション履歴</h2>
        </div>

        {!detail.actions || detail.actions.length === 0 ? (
          <p className="p-6 text-center text-sm text-slate-500">アクション履歴なし</p>
        ) : (
          <div className="divide-y divide-slate-100">
            {detail.actions.map((action, i) => {
              const meta = actionTypeLabel[action.actionType] ?? {
                label: action.actionType,
                icon: '•',
              };
              return (
                <div key={i} className="flex items-center gap-3 px-5 py-3">
                  <span className="text-lg">{meta.icon}</span>
                  <span className="text-sm text-slate-700">{meta.label}</span>
                  <span className="ml-auto text-xs text-slate-400">
                    {formatTime(action.timestamp)}
                  </span>
                </div>
              );
            })}
          </div>
        )}
      </section>

      <Link to="/logs/chat" className="inline-block text-sm text-blue-600 hover:text-blue-800">
        ← チャットログ一覧に戻る
      </Link>
    </div>
  );
};
